import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, DollarSign, CheckCircle, Clock, XCircle, Upload, ChevronRight } from 'lucide-react';

export default function PettyCash() {
  const { user, token } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [paymentBanks, setPaymentBanks] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'USD',
    purpose: '',
    type: 'cash',
    attachment: null,
    payee_name: '',
    payee_mobile: '',
    payee_id_card: '',
    payee_account_name: '',
    payee_bank_name: ''
  });
  const [completionSlip, setCompletionSlip] = useState(null);

  const canApproveVerify = user?.permissions?.includes('pettycash_approve_verify') || user?.permissions?.includes('all');
  const canCreate = user?.permissions?.includes('pettycash_create') || user?.permissions?.includes('all');

  useEffect(() => {
    if (canApproveVerify) {
      fetchAllRequests();
    }
    if (canCreate) {
      fetchMyRequests();
    }
    fetchPaymentBanks();
  }, []);

  const fetchAllRequests = async () => {
    try {
      const response = await axios.get(`${API}/petty-cash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch petty cash requests:', error);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const response = await axios.get(`${API}/petty-cash/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch my requests:', error);
    }
  };

  const fetchPaymentBanks = async () => {
    try {
      const response = await axios.get(`${API}/payment-banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentBanks(response.data);
    } catch (error) {
      console.error('Failed to fetch payment banks:', error);
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (field === 'attachment') {
        setFormData({ ...formData, attachment: reader.result });
      } else if (field === 'completion_slip') {
        setCompletionSlip(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/petty-cash`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Petty cash request created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchMyRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedBankId || !selectedAccount) {
      toast.error('Please select a bank and account');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/petty-cash/${selectedRequest.id}/approve`, 
        { bank_id: selectedBankId, bank_account: selectedAccount },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Request approved successfully');
      setShowApproveDialog(false);
      setSelectedRequest(null);
      setSelectedBankId('');
      setSelectedAccount('');
      fetchAllRequests();
      fetchPaymentBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (requestId) => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/petty-cash/${requestId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Request verified! Voucher ${response.data.voucher_id} created`);
      fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify request');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (requestId) => {
    setLoading(true);
    try {
      await axios.put(`${API}/petty-cash/${requestId}/complete`, 
        { completion_slip: completionSlip },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Request completed successfully');
      setCompletionSlip(null);
      fetchMyRequests();
      if (canApproveVerify) fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete request');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      currency: 'USD',
      purpose: '',
      type: 'cash',
      attachment: null,
      payee_name: '',
      payee_mobile: '',
      payee_id_card: '',
      payee_account_name: '',
      payee_bank_name: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'verified': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  // Get the selected bank object and filter its accounts by request currency
  const selectedBankObj = paymentBanks.find(b => b.id === selectedBankId);
  const filteredAccounts = selectedBankObj
    ? selectedBankObj.accounts.filter(acc => acc.currency === selectedRequest?.currency)
    : [];

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Petty Cash</h1>
            <p className="text-slate-600 mt-1">Manage petty cash requests and approvals</p>
          </div>
          {canCreate && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Request Petty Cash
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Petty Cash Request</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateRequest} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Amount *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Currency *</Label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        required
                      >
                        <option value="USD">USD ($)</option>
                        <option value="MVR">MVR (ރ.)</option>
                        <option value="CHN">CHN (¥)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>Purpose *</Label>
                    <Textarea
                      value={formData.purpose}
                      onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>Type *</Label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    >
                      <option value="cash">Cash</option>
                      <option value="transfer">Transfer</option>
                    </select>
                  </div>

                  {formData.type === 'cash' ? (
                    <>
                      <div>
                        <Label>Payee Name *</Label>
                        <Input
                          value={formData.payee_name}
                          onChange={(e) => setFormData({ ...formData, payee_name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Mobile Number *</Label>
                          <Input
                            value={formData.payee_mobile}
                            onChange={(e) => setFormData({ ...formData, payee_mobile: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>ID Card Number *</Label>
                          <Input
                            value={formData.payee_id_card}
                            onChange={(e) => setFormData({ ...formData, payee_id_card: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <Label>Payee Account Name *</Label>
                        <Input
                          value={formData.payee_account_name}
                          onChange={(e) => setFormData({ ...formData, payee_account_name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Payee Bank Name *</Label>
                          <Input
                            value={formData.payee_bank_name}
                            onChange={(e) => setFormData({ ...formData, payee_bank_name: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>Payee ID Card Number *</Label>
                          <Input
                            value={formData.payee_id_card}
                            onChange={(e) => setFormData({ ...formData, payee_id_card: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <Label>Attachment (Optional)</Label>
                    <Input
                      type="file"
                      onChange={(e) => handleFileUpload(e, 'attachment')}
                      accept="image/*,.pdf"
                    />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full">
                    {loading ? 'Creating...' : 'Submit Request'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* My Requests */}
        {canCreate && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">My Requests</h2>
            <div className="grid gap-4">
              {myRequests.map((request) => (
                <Card key={request.id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <DollarSign className="w-5 h-5 text-slate-600" />
                        <h3 className="text-lg font-bold">{request.request_number}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(request.status)}`}>
                          {request.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-slate-600">
                        <p><strong>Amount:</strong> {request.currency} {request.amount}</p>
                        <p><strong>Purpose:</strong> {request.purpose}</p>
                        <p><strong>Type:</strong> {request.type}</p>
                        {request.voucher_id && (
                          <p className="text-green-600"><strong>Voucher:</strong> {request.voucher_id}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {getStatusIcon(request.status)}
                      {request.status === 'verified' && (
                        <div>
                          <Label className="text-xs">Upload Slip</Label>
                          <Input
                            type="file"
                            onChange={(e) => handleFileUpload(e, 'completion_slip')}
                            accept="image/*,.pdf"
                            className="text-xs"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleComplete(request.id)}
                            disabled={!completionSlip || loading}
                            className="mt-2 w-full"
                          >
                            Complete
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
              {myRequests.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <p>No requests yet. Create your first petty cash request!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* All Requests (Admin View) */}
        {canApproveVerify && (
          <div>
            <h2 className="text-xl font-bold mb-4">All Requests</h2>
            <div className="grid gap-4">
              {requests.map((request) => (
                <Card key={request.id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <DollarSign className="w-5 h-5 text-slate-600" />
                        <h3 className="text-lg font-bold">{request.request_number}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(request.status)}`}>
                          {request.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-slate-600">
                        <p><strong>Requested by:</strong> {request.requester_name}</p>
                        <p><strong>Amount:</strong> {request.currency} {request.amount}</p>
                        <p><strong>Purpose:</strong> {request.purpose}</p>
                        <p><strong>Type:</strong> {request.type}</p>
                        <p><strong>Payee:</strong> {request.payee_name || request.payee_account_name}</p>
                        {request.bank_account && (
                          <p><strong>Bank Account:</strong> {request.bank_account}</p>
                        )}
                        {request.voucher_id && (
                          <p className="text-green-600"><strong>Voucher:</strong> {request.voucher_id}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {request.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowApproveDialog(true);
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Approve
                        </Button>
                      )}
                      {request.status === 'approved' && (
                        <Button
                          size="sm"
                          onClick={() => handleVerify(request.id)}
                          disabled={loading}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          Verify
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
              {requests.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <p>No petty cash requests found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onOpenChange={(open) => {
          setShowApproveDialog(open);
          if (!open) { setSelectedBankId(''); setSelectedAccount(''); }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Petty Cash Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Request: {selectedRequest?.request_number}<br />
                Amount: {selectedRequest?.currency} {selectedRequest?.amount}
              </p>

              {/* Step 1: Select Bank */}
              <div>
                <Label>Select Bank *</Label>
                <div className="space-y-2 mt-2">
                  {paymentBanks.map((bank) => {
                    const hasMatchingAccounts = bank.accounts.some(acc => acc.currency === selectedRequest?.currency);
                    return (
                      <div
                        key={bank.id}
                        data-testid={`bank-option-${bank.id}`}
                        onClick={() => {
                          if (hasMatchingAccounts) {
                            setSelectedBankId(bank.id);
                            setSelectedAccount('');
                          }
                        }}
                        className={`p-3 border rounded cursor-pointer transition-colors ${
                          !hasMatchingAccounts ? 'opacity-40 cursor-not-allowed' :
                          selectedBankId === bank.id ? 'border-blue-500 bg-blue-50' : 'hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {bank.bank_logo && (
                            <img src={bank.bank_logo} alt="" className="w-8 h-8 object-contain rounded" />
                          )}
                          <div className="flex-1">
                            <p className="font-semibold">{bank.bank_name}</p>
                            {!hasMatchingAccounts && (
                              <p className="text-xs text-red-500">No {selectedRequest?.currency} accounts</p>
                            )}
                          </div>
                          {selectedBankId === bank.id && (
                            <ChevronRight className="w-4 h-4 text-blue-500" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {paymentBanks.length === 0 && (
                    <p className="text-sm text-slate-500 py-2">No banks configured. Add banks in Settings.</p>
                  )}
                </div>
              </div>

              {/* Step 2: Select Account (shown after bank is selected) */}
              {selectedBankId && (
                <div>
                  <Label>Select {selectedRequest?.currency} Account *</Label>
                  <div className="space-y-2 mt-2">
                    {filteredAccounts.map((account) => (
                      <div
                        key={account.account_number}
                        data-testid={`account-option-${account.account_number}`}
                        onClick={() => setSelectedAccount(account.account_number)}
                        className={`p-3 border rounded cursor-pointer transition-colors ${
                          selectedAccount === account.account_number ? 'border-blue-500 bg-blue-50' : 'hover:border-blue-300'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{account.account_number}</p>
                            <p className="text-sm text-slate-600">{selectedBankObj?.bank_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{account.currency} {account.balance.toLocaleString()}</p>
                            <p className="text-xs text-slate-500">Balance</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredAccounts.length === 0 && (
                      <p className="text-sm text-slate-500 py-2">No {selectedRequest?.currency} accounts in this bank.</p>
                    )}
                  </div>
                </div>
              )}

              <Button
                data-testid="approve-request-btn"
                onClick={handleApprove}
                disabled={!selectedBankId || !selectedAccount || loading}
                className="w-full"
              >
                {loading ? 'Approving...' : 'Approve Request'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}